import { useEffect } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router'
import { telemetry } from '@/app/telemetry'
import { Layout } from '@/shell/Layout'
import { AccessProvider } from './AccessContext'
import { getCurrentCondo, setCurrentCondo } from './currentCondo'
import { useMemberships } from './memberships'
import { ContextPicker, ErrorScreen, LoadingScreen, NoAccessScreen } from './screens'
import { managerContexts } from './sections'

/** Loads memberships once for everything below; resident-only users stop here. */
export function AccessGate() {
  const { data: me, isPending, isError, refetch } = useMemberships()
  if (isPending) return <LoadingScreen />
  if (isError) return <ErrorScreen onRetry={() => void refetch()} />
  if (managerContexts(me).length === 0) return <NoAccessScreen />
  return <Outlet />
}

/** `/`: reopen the last condominium, auto-select the only one, or ask. */
export function ContextEntry() {
  const { data: me } = useMemberships()
  const navigate = useNavigate()
  const contexts = me ? managerContexts(me) : []
  const stored = getCurrentCondo()
  const target =
    contexts.find((c) => c.condominium_id === stored) ??
    (contexts.length === 1 ? contexts[0] : undefined)

  useEffect(() => {
    if (target) navigate(`/c/${target.condominium_id}`, { replace: true })
  }, [target, navigate])

  if (target || !me) return <LoadingScreen />
  return (
    <ContextPicker contexts={contexts} isRoot={me.is_root} onPick={(id) => navigate(`/c/${id}`)} />
  )
}

/** `/c/:condoId/*`: guards the condominium, remembers it and renders the shell. */
export function CondoRoute() {
  const { data: me } = useMemberships()
  const params = useParams()
  const navigate = useNavigate()
  const condoId = Number(params.condoId)
  const valid = !!me && managerContexts(me).some((c) => c.condominium_id === condoId)

  useEffect(() => {
    if (valid) setCurrentCondo(condoId)
    // Lost access to this condominium (e.g. after a refetch): back to the picker.
    else if (me) navigate('/', { replace: true })
  }, [valid, me, condoId, navigate])

  useEffect(() => {
    if (!me || !valid) return
    telemetry.setAccessContext({
      condominiumId: condoId,
      capabilities: me.memberships.find((m) => m.condominium_id === condoId)?.capabilities ?? [],
    })
  }, [me, valid, condoId])

  if (!me || !valid) return <LoadingScreen />
  return (
    <AccessProvider me={me} condoId={condoId}>
      <Layout />
    </AccessProvider>
  )
}
